/*
  Klaviyo subscribe + identify handler.

  Hijacks submission of any <form data-klaviyo-subscribe="LIST_ID"> in the
  theme. Use cases today:
    - sections/newsletter.liquid          (Get the Squeeze, home page)
    - snippets/alt-nav-drawer.liquid      (Mobile nav drawer subscribe)

  Flow on submit:
    1. preventDefault so Shopify's customer form doesn't navigate away.
    2. POST to Klaviyo Client Subscriptions API
       (https://a.klaviyo.com/client/subscriptions/?company_id=PUBLIC_KEY).
       Klaviyo handles single- vs double-opt-in per the list's own settings.
    3. Call window.klaviyo.identify({ email }) so the visitor is now
       cookied/identified for the rest of the session. Falls back to the
       legacy _learnq queue if the new SDK surface isn't loaded yet.
    4. If the form opts in via data-klaviyo-shopify-fallback, fire-and-forget
       a POST to Shopify's own customer-form action so the merchant still
       gets a customer row with the "newsletter" tag in Shopify Admin. We
       deliberately don't await this — Klaviyo is the source of truth; the
       Shopify row is a courtesy.
    5. Show inline success/error status inside the form.

  Configuration precedence per form:
    data-klaviyo-company-id     > [defaults set in markup from theme settings]
    data-klaviyo-subscribe      > [list id, required]
    data-klaviyo-source         > "Theme newsletter form"
    data-klaviyo-success        > default success copy
    data-klaviyo-error          > default error copy

  Re-binds after Shopify theme-editor section reloads so designers see the
  Klaviyo behavior in preview without a full page refresh.
*/

(function () {
  'use strict';

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  /*
    Pin the revision header explicitly: Klaviyo treats the absence of this
    header as "use the oldest still-supported revision", which is risky
    long-term. Bump this when we test against a newer revision. The Client
    Subscriptions endpoint hasn't broken across revisions historically.
  */
  var KLAVIYO_REVISION = '2024-10-15';

  var DEFAULT_SUCCESS = "You're in! Watch your inbox.";
  var DEFAULT_ERROR = 'Something went wrong. Please try again.';

  function subscribeToKlaviyo(email, companyId, listId, sourceLabel) {
    var url = KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId);
    /*
      Body shape for the Client Subscriptions endpoint (revision
      2024-10-15). Do NOT add a `subscriptions` block under profile.attributes
      — older Klaviyo docs sometimes show it, but the current API rejects
      with 400 "subscriptions is not a valid field for the resource
      profile". POSTing to /client/subscriptions/ already conveys consent;
      the list-level opt-in setting in Klaviyo (single vs double) is what
      governs whether a confirmation email is sent before the profile
      becomes SUBSCRIBED.
    */
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: sourceLabel || 'Theme newsletter form',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      /*
        Klaviyo returns 202 Accepted on success for this endpoint. Treat
        any 2xx as success; bubble the body text on failure for the
        console so we can debug list-id mismatches etc.
      */
      if (res.status >= 200 && res.status < 300) {
        return true;
      }
      return res.text().then(function (text) {
        throw new Error('Klaviyo subscribe failed (' + res.status + '): ' + text);
      });
    });
  }

  function identifyInKlaviyo(email) {
    /*
      Two SDK surfaces depending on when the onsite script finished
      loading: the modern global `klaviyo` object exposes identify()
      directly; the legacy `_learnq` array still queues commands until
      the SDK boots and replays them. We try the new surface first.
    */
    try {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify({ email: email });
        return;
      }
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['identify', { $email: email }]);
        return;
      }
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['identify', { $email: email }]);
      }
    } catch (_) {
      /* identify() is fire-and-forget; never block the success UX on it */
    }
  }

  function submitToShopifyBestEffort(form) {
    /*
      The original Liquid `{% form 'customer' %}` already rendered a real
      form with `action="/contact#contact_form"` and an authenticity
      token. Re-submitting it via fetch (no navigation) creates the
      Shopify customer row exactly as the default flow would, without
      disturbing the Klaviyo success UI. We swallow errors — the Klaviyo
      subscribe is the contract; Shopify is bookkeeping.

      Encoding gotcha: passing a FormData instance to fetch() sets
      Content-Type to multipart/form-data with a generated boundary.
      Shopify's /contact endpoint rejects that with a 400 — it expects
      application/x-www-form-urlencoded (the default <form> enctype).
      We convert FormData -> URLSearchParams which causes fetch() to
      set the correct content-type automatically. (Also handles array
      values, which the customer form doesn't have today but might if
      we add multi-tag inputs later.)

      Alternative path the merchant can take: if the Klaviyo<->Shopify
      app integration is configured to "Sync subscribers to Shopify",
      Klaviyo will create the customer row asynchronously on its own.
      In that case the merchant can turn off the
      `klaviyo_also_create_shopify_customer` setting and this code
      becomes dead. See README "Klaviyo signup setup" for guidance.
    */
    if (!form || !form.action) return;
    try {
      var fd = new FormData(form);
      var params = new URLSearchParams();
      fd.forEach(function (value, key) {
        params.append(key, typeof value === 'string' ? value : '');
      });
      fetch(form.action, {
        method: form.method && form.method.toLowerCase() === 'get' ? 'GET' : 'POST',
        body: params,
        headers: { accept: 'text/html' },
        credentials: 'same-origin',
      }).catch(function () {});
    } catch (_) {
      /* swallow */
    }
  }

  function ensureStatusEl(form) {
    var host = form.querySelector('[data-klaviyo-status]');
    if (host) return host;
    host = document.createElement('div');
    host.setAttribute('data-klaviyo-status', '');
    host.className = 'wt-klaviyo-status';
    host.setAttribute('aria-live', 'polite');
    form.appendChild(host);
    return host;
  }

  function setStatus(form, kind, message) {
    var host = ensureStatusEl(form);
    host.classList.remove(
      'wt-klaviyo-status--error',
      'wt-klaviyo-status--success',
      'wt-klaviyo-status--loading'
    );
    if (kind) host.classList.add('wt-klaviyo-status--' + kind);
    host.textContent = message || '';
  }

  function setSubmitting(form, submitting) {
    form.classList.toggle('wt-klaviyo--loading', !!submitting);
    var btn = form.querySelector('button[type="submit"]');
    if (!btn) return;
    if (submitting) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  }

  function handleSubmit(event) {
    var form = event.currentTarget;
    var companyId = (form.dataset.klaviyoCompanyId || '').trim();
    var listId = (form.dataset.klaviyoSubscribe || '').trim();

    /*
      Soft fallback: if the merchant hasn't filled in theme settings yet,
      let the form submit normally to Shopify rather than blackholing the
      lead. The console warn surfaces this during setup.
    */
    if (!companyId || !listId) {
      if (window.console && console.warn) {
        console.warn(
          '[klaviyo-subscribe] Missing company id or list id; falling back to default form submit.'
        );
      }
      return;
    }

    event.preventDefault();

    var emailInput =
      form.querySelector('input[type="email"]') ||
      form.querySelector('input[name="contact[email]"]');
    var email = emailInput ? (emailInput.value || '').trim() : '';
    if (!email) return;

    var sourceLabel = form.dataset.klaviyoSource || '';
    var successMsg = form.dataset.klaviyoSuccess || DEFAULT_SUCCESS;
    var errorMsg = form.dataset.klaviyoError || DEFAULT_ERROR;
    var shouldShopify = form.dataset.klaviyoShopifyFallback === 'true';

    setSubmitting(form, true);
    setStatus(form, 'loading', '');

    subscribeToKlaviyo(email, companyId, listId, sourceLabel)
      .then(function () {
        identifyInKlaviyo(email);
        if (shouldShopify) submitToShopifyBestEffort(form);

        form.classList.add('wt-klaviyo--success');
        form.classList.remove('wt-klaviyo--error');
        showSuccessState(form, successMsg);
      })
      .catch(function (err) {
        form.classList.add('wt-klaviyo--error');
        form.classList.remove('wt-klaviyo--success');
        if (window.console && console.error) console.error('[klaviyo-subscribe]', err);
        setStatus(form, 'error', errorMsg);
      })
      .then(function () {
        setSubmitting(form, false);
      });
  }

  function neutralizeProgrammaticSubmit(form) {
    /*
      Third-party scripts (Facebook Pixel's fbevents.js is the confirmed
      culprit, but Pinterest tag, TikTok pixel, and various session
      replay tools do the same thing) auto-detect forms and call
      form.submit() programmatically when they observe a submit event,
      trying to fire their own "Lead" tracking. form.submit() bypasses
      addEventListener('submit') handlers AND event.preventDefault(),
      so the form actually navigates to its action URL even though our
      handler called preventDefault.

      Defense: replace the .submit() method on THIS form instance with
      a no-op. Native browser submission (button click, Enter key)
      still works because it dispatches a submit *event* — which our
      addEventListener catches — and never calls the .submit() method.
      Only programmatic submitters get neutralized, which is the bug
      we want to fix.

      We also stop reuse of the form's `action` attribute as a
      navigation hint: a fetch from submitToShopifyBestEffort still
      reads it via form.action getter (works), but our form is no
      longer a target for redirect-on-submit-style helpers.
    */
    try {
      Object.defineProperty(form, 'submit', {
        configurable: true,
        writable: true,
        value: function () {
          /*
            Silently swallow programmatic submits. We don't warn here
            because Facebook's pixel calls this every time and the
            console noise would be alarming. Anyone debugging will
            see this defineProperty in the source.
          */
        },
      });
    } catch (_) {
      /*
        Some older browsers (or pages with frozen prototypes) reject
        defineProperty on instance. Fall back to direct overwrite;
        it's not 100% bulletproof but covers the common case.
      */
      try {
        form.submit = function () {};
      } catch (__) {}
    }
  }

  function showSuccessState(form, message) {
    /*
      On Klaviyo success, swap the input+button row out entirely for
      a high-contrast confirmation chip. The legacy behavior was a
      small inline status line under the button — the merchant
      reported it was easy to miss against the dark blue panel, AND
      a follow-up submit (Facebook's programmatic submit) could
      append a Shopify error message in the same spot, making the
      success ambiguous. Replacing the form contents makes the
      successful state unmistakable.
    */
    var fieldset =
      form.querySelector('.wt-newsletter__fieldset') ||
      form.querySelector('.wt-alt-drawer__subscribe-form');
    var existingStatus = form.querySelector('[data-klaviyo-status]');
    if (existingStatus) existingStatus.remove();

    var success = document.createElement('div');
    success.className = 'wt-klaviyo-success';
    success.setAttribute('role', 'status');
    success.setAttribute('aria-live', 'polite');
    success.innerHTML =
      '<svg class="wt-klaviyo-success__icon" aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
      '<span class="wt-klaviyo-success__text"></span>';
    success.querySelector('.wt-klaviyo-success__text').textContent = message;

    if (fieldset) {
      fieldset.style.display = 'none';
      fieldset.parentNode.insertBefore(success, fieldset.nextSibling);
    } else {
      form.appendChild(success);
    }
  }

  function bind(form) {
    if (!form || form.dataset.klaviyoBound === '1') return;
    form.dataset.klaviyoBound = '1';
    form.addEventListener('submit', handleSubmit);
    neutralizeProgrammaticSubmit(form);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var forms = scope.querySelectorAll('form[data-klaviyo-subscribe]');
    for (var i = 0; i < forms.length; i++) bind(forms[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /*
    Theme-editor preview: section reloads replace DOM nodes, so any form
    we bound is now orphaned. Re-scan on section:load. (alt-nav-drawer is
    a snippet, not a section, but it's rendered inside the header section
    and gets refreshed with it.)
  */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
